import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Grid } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useAuth } from "../../../context/AuthContext";
import { toAbvPercent } from "../../../services/drinks/utils/numbers";
import { useOnlineStatus, useOutbox } from "@dowty/offline";
import { useNavigate, useLocation } from "react-router-dom";
import { DATE_TIME_FORMAT, DEFAULT_DRINK_DURATION_MINUTES } from "../drinkStats/constants";
import { useTranslation } from "react-i18next";
import { outbox } from "../../../offline";
import { useAddDrinkData } from "./useAddDrinkData";
import { useAddDrinkSubmit } from "./useAddDrinkSubmit";
const { useBreakpoint } = Grid;
export type AmountPresetKey = "custom" | "shot" | "double" | "longdrink" | "beer" | "mass";

export const AMOUNT_PRESETS: Record<
    AmountPresetKey,
    { labelKey: string; subtitleKey?: string; amount: number | null }
> = {
    shot: { labelKey: "addDrink.amountPresets.shot", subtitleKey: "addDrink.amountPresets.shotSubtitle", amount: 20 },
    double: { labelKey: "addDrink.amountPresets.double", subtitleKey: "addDrink.amountPresets.doubleSubtitle", amount: 40 },
    longdrink: { labelKey: "addDrink.amountPresets.longdrink", subtitleKey: "addDrink.amountPresets.longdrinkSubtitle", amount: 300 },
    beer: { labelKey: "addDrink.amountPresets.beer", subtitleKey: "addDrink.amountPresets.beerSubtitle", amount: 500 },
    mass: { labelKey: "addDrink.amountPresets.mass", subtitleKey: "addDrink.amountPresets.massSubtitle", amount: 1000 },
    custom: { labelKey: "addDrink.amountPresets.custom", subtitleKey: "addDrink.amountPresets.customSubtitle", amount: null },
};

export const DURATION_PRESETS: Array<{ labelKey: string; minutes: number }> = [
    { labelKey: "addDrink.durationPresets.instant", minutes: 0 },
    { labelKey: "addDrink.durationPresets.quarterHour", minutes: 15 },
    { labelKey: "addDrink.durationPresets.halfHour", minutes: 30 },
    { labelKey: "addDrink.durationPresets.threeQuarterHour", minutes: 45 },
    { labelKey: "addDrink.durationPresets.oneHour", minutes: 60 },
];

type AddDrinkProps = {
    embedded?: boolean;
    onRequestClose?: () => void;
};


export const useAddDrink = ({ embedded = false, onRequestClose }: AddDrinkProps = {}) => {
    const { isAuthenticated, user } = useAuth();
    const [selectedDrinkId, setSelectedDrinkId] = useState<number | null>(null);
    const [amountMl, setAmountMl] = useState<number>(500);
    const [selectedPreset, setSelectedPreset] = useState<AmountPresetKey>("beer");
    const [consumedAt, setConsumedAt] = useState<Dayjs | null>(dayjs());
    const [durationMinutes, setDurationMinutes] = useState<number>(DEFAULT_DRINK_DURATION_MINUTES);
    const [selectedDurationPreset, setSelectedDurationPreset] = useState<number | "custom">(DEFAULT_DRINK_DURATION_MINUTES);
    const [startAtOverride, setStartAtOverride] = useState<Dayjs | null>(null);
   const [lastAddedDrink, setLastAddedDrink] = useState<{ name: string; amount: number } | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const screens = useBreakpoint();
    const isOnline = useOnlineStatus();
    const { entries: outboxEntries, pendingCount, failedCount } = useOutbox(outbox);
    const { drinks, fetching, drinksFromCache, drinksError, recentActivities, recentTemplates, recentTemplatesLoading, recentTemplatesError, reloadRecentActivities } = useAddDrinkData(user, isAuthenticated);
    const repeatApplied = useRef(false);


    const fallbackIsMobile = typeof window !== "undefined" ? window.innerWidth <= 768 : false;
    const isMobile = typeof screens.md === "boolean" ? !screens.md : fallbackIsMobile;
    const modalEnabled = !embedded;
    const scrollPadding = isMobile ? 120 : 48;
    const [mobileModalVisible, setMobileModalVisible] = useState<boolean>(modalEnabled);
    const [lockBodyScroll, setLockBodyScroll] = useState<boolean>(false);
    const previousBodyOverflow = useRef<string | null>(null);
    const previousPathRef = useRef(location.pathname);

    useEffect(() => {
        if (!lastAddedDrink || typeof window === "undefined") {
            return;
        }
        const timeout = window.setTimeout(() => setLastAddedDrink(null), 4500);
        return () => window.clearTimeout(timeout);
    }, [lastAddedDrink]);

    useEffect(() => {
        if (previousPathRef.current === location.pathname) {
            return;
        }
        previousPathRef.current = location.pathname;
        if (embedded) {
            onRequestClose?.();
        } else if (modalEnabled) {
            setMobileModalVisible(false);
        }
    }, [embedded, location.pathname, modalEnabled, onRequestClose]);

    useEffect(() => {
        setMobileModalVisible(modalEnabled);
    }, [modalEnabled]);

    useEffect(() => {
        if (typeof document === "undefined") {
            return;
        }
        if (lockBodyScroll) {
            if (previousBodyOverflow.current === null) {
                previousBodyOverflow.current = document.body.style.overflow || "";
            }
            document.body.style.overflow = "hidden";
        }
        return () => {
            if (previousBodyOverflow.current !== null) {
                document.body.style.overflow = previousBodyOverflow.current;
                previousBodyOverflow.current = null;
            }
        };
    }, [lockBodyScroll]);

    const handlePresetChange = (value: AmountPresetKey) => {
        setSelectedPreset(value);
        const preset = AMOUNT_PRESETS[value];
        if (preset.amount != null) {
            setAmountMl(preset.amount);
        }
    };

    const handleAmountInputChange = useCallback((value: number | null = 0) => {
        const sanitized = value ?? 0;
        setAmountMl(sanitized);
        if (sanitized <= 0) {
            setSelectedPreset("custom");
            return;
        }
        const matchedEntry = (Object.entries(AMOUNT_PRESETS) as [AmountPresetKey, { amount: number | null }][]).find(
            ([, preset]) => preset.amount === sanitized
        );
        setSelectedPreset(matchedEntry ? matchedEntry[0] : "custom");
    }, []);

    useEffect(() => {
        if (repeatApplied.current || new URLSearchParams(location.search).get("repeat") !== "last" || fetching || recentTemplatesLoading) return;
        repeatApplied.current = true;
        const latest = recentActivities.find((activity) => drinks.some((drink) => drink.id === activity.drinkId));
        if (!latest || typeof latest.drinkId !== "number") return;
        setSelectedDrinkId(latest.drinkId);
        handleAmountInputChange(latest.amountMl);
    }, [location.search, fetching, recentTemplatesLoading, recentActivities, drinks, handleAmountInputChange]);

    const handleDurationPresetChange = (value: number | "custom") => {
        setSelectedDurationPreset(value);
        if (value !== "custom") {
            setDurationMinutes(value);
            setStartAtOverride(null);
        }
    };

    const handleDurationInputChange = (value: number | null) => {
        const minutes = Math.max(0, value ?? 0);
        setDurationMinutes(minutes);
        setSelectedDurationPreset("custom");
        setStartAtOverride(null);
    };

    const handleStartChange = (value: Dayjs | null) => {
        if (!value) {
            setStartAtOverride(null);
            return;
        }
        setStartAtOverride(value);
        if (consumedAt) {
            const diff = Math.max(0, consumedAt.diff(value, "minute"));
            setDurationMinutes(diff);
            setSelectedDurationPreset("custom");
        }
    };

    const drinkOptions = useMemo(
        () =>
            drinks.map((drink) => ({
                value: drink.id,
                label: `${drink.name} (${toAbvPercent(drink.promille).toFixed(1)}% ABV)`,
            })),
        [drinks]
    );
    const derivedStart = useMemo(() => {
        if (startAtOverride) {
            return startAtOverride;
        }
        if (!consumedAt) {
            return null;
        }
        return consumedAt.subtract(durationMinutes || 0, "minute");
    }, [startAtOverride, consumedAt, durationMinutes]);

    const selectedDurationMeta = useMemo(() => {
        if (typeof selectedDurationPreset !== "number") {
            return null;
        }
        const preset = DURATION_PRESETS.find((item) => item.minutes === selectedDurationPreset);
        if (preset) {
            return {
                label: t(preset.labelKey),
                minutes: preset.minutes,
            };
        }
        return {
            label: t("addDrink.form.durationPresetMinutes", { minutes: selectedDurationPreset }),
            minutes: selectedDurationPreset,
        };
    }, [selectedDurationPreset, t]);

    const getDurationHint = () => {
        if (!consumedAt) {
            return t("addDrink.form.durationHintNoFinish");
        }
        if (durationMinutes === 0) {
            return t("addDrink.form.durationHintChug");
        }
        return t("addDrink.form.durationHintStartAt", {
            timestamp: (derivedStart ?? consumedAt).format(DATE_TIME_FORMAT),
        });
    };

    const handleCloseModal = useCallback(() => {
        setMobileModalVisible(false);
        if (embedded) {
            onRequestClose?.();
            return;
        }
        if (modalEnabled) {
            if (typeof window !== "undefined" && window.history.length > 1) {
                navigate(-1);
            } else {
                navigate("/");
            }
        }
    }, [embedded, modalEnabled, navigate, onRequestClose]);

    const triggerPostAddCleanup = useCallback(() => {
        handleCloseModal();
        void reloadRecentActivities();
    }, [handleCloseModal, reloadRecentActivities]);

    const { handleSubmit, submitting } = useAddDrinkSubmit({
        user, t, drinks, selectedDrinkId, setSelectedDrinkId, amountMl, setAmountMl, consumedAt, durationMinutes, startAtOverride,
        derivedStart, isOnline, setLastAddedDrink, setSelectedPreset, setConsumedAt, setDurationMinutes,
        setSelectedDurationPreset, setStartAtOverride, triggerPostAddCleanup,
    });
    const popupParent = (triggerNode: HTMLElement | undefined) =>
        triggerNode?.parentElement ?? document.body;

    return {
        embedded, isAuthenticated, user, navigate, t, i18n, drinks, fetching, drinksFromCache, drinksError, isOnline,
        pendingCount, failedCount, outboxEntries, lastAddedDrink, setLastAddedDrink, handleSubmit, drinkOptions,
        selectedDrinkId, setSelectedDrinkId, setDurationMinutes, setSelectedDurationPreset, handlePresetChange, selectedPreset, amountMl,
        handleAmountInputChange, consumedAt, setConsumedAt, startAtOverride, setStartAtOverride,
        durationMinutes, derivedStart, handleStartChange, isMobile, setLockBodyScroll,
        popupParent, selectedDurationPreset, handleDurationPresetChange, handleDurationInputChange,
        getDurationHint, selectedDurationMeta, submitting, recentTemplates, recentTemplatesLoading, recentTemplatesError, recentActivities,
        triggerPostAddCleanup, reloadRecentActivities, mobileModalVisible, setMobileModalVisible,
        handleCloseModal, scrollPadding,
    };
};
